import React, { useState } from 'react';
import { updateEntity, friendlyError } from '../data/entities';

// The hosted instance and the App Builder design are different records.
// `app.links.instanceAppId` is the only field that joins them. Lab targets,
// sync, and Operate all read that field.

export function designLabel(d, all) {
  const name = d?.name || 'Untitled';
  const dup = (all || []).some((x) => x.id !== d.id && x.name === d.name);
  if (!dup) return name;
  if (d.journey?.step) return `${name} · ${d.journey.step}`;
  return `${name} · ${String(d.id || '').slice(0, 6)}`;
}

export function InstanceLinkSelect({ orgId, app, instances, canEdit, ariaLabel = 'Hosted instance for this design' }) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Only instances a design can ship to: not UAT twins, not retired, and not
  // the warm pool (pool-* slugs, claimed/consumed rows) that provisioning
  // owns — those used to show up here and were picked by mistake (audit P1-1).
  const prod = (instances || []).filter((i) => i.envRole !== 'uat' && !['terminated', 'consumed', 'claimed'].includes(i.status) && !/^pool-/.test(i.slug || ''));
  const current = app?.links?.instanceAppId || '';
  const link = async (id) => {
    if (!app) return;
    setBusy(true);
    setErr('');
    try {
      await updateEntity('app', { org: orgId }, app.id, {
        links: { ...(app.links || {}), instanceAppId: id || null },
      });
    } catch (e) {
      setErr(friendlyError(e));
    }
    setBusy(false);
  };
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select className="cs-input" aria-label={ariaLabel} value={current} disabled={!canEdit || busy || !app} onChange={(e) => link(e.target.value)} style={{ maxWidth: 320 }}>
        <option value="">Not linked</option>
        {current && !prod.some((i) => i.id === current) && <option value={current}>Linked instance</option>}
        {prod.map((i) => <option key={i.id} value={i.id}>{i.name || i.slug}{i.slug && i.name ? ` (${i.slug})` : ''} · {i.status || 'draft'}</option>)}
      </select>
      {err && <span className="cs-form-error">{err}</span>}
    </span>
  );
}

// From the instance's own page: which App Builder design this hosted app belongs to.
export function DesignLinkSelect({ orgId, instance, designs, canEdit }) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const list = designs || [];
  const linked = list.filter((d) => d.links?.instanceAppId === instance?.id);
  const value = linked.length === 1 ? linked[0].id : '';
  const link = async (designId) => {
    if (!instance) return;
    setBusy(true);
    setErr('');
    try {
      for (const d of linked) {
        if (d.id === designId) continue;
        await updateEntity('app', { org: orgId }, d.id, { links: { ...(d.links || {}), instanceAppId: null } });
      }
      if (designId) {
        const d = list.find((x) => x.id === designId);
        if (d && d.links?.instanceAppId !== instance.id) {
          await updateEntity('app', { org: orgId }, d.id, { links: { ...(d.links || {}), instanceAppId: instance.id } });
        }
      }
    } catch (e) {
      setErr(friendlyError(e));
    }
    setBusy(false);
  };
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select className="cs-input" aria-label="App Builder design for this instance" value={value} disabled={!canEdit || busy} onChange={(e) => link(e.target.value)} style={{ maxWidth: 320 }}>
        <option value="">{linked.length > 1 ? `${linked.length} designs` : 'Not linked'}</option>
        {list.map((d) => <option key={d.id} value={d.id}>{designLabel(d, list)}</option>)}
      </select>
      {err && <span className="cs-form-error">{err}</span>}
    </span>
  );
}
